import {
  ArrowDown,
  ArrowUp,
  BarChart3,
  CheckCircle2,
  Film,
  FolderTree,
  Loader2,
  LogOut,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Shield,
  Trash2,
  Upload,
  Users
} from "lucide-react";
import cloudbase from "@cloudbase/js-sdk";
import { useEffect, useMemo, useRef, useState } from "react";

const API_BASE_URL = (import.meta.env.VITE_ADMIN_API_BASE_URL || "").replace(/\/+$/, "");
const CLOUDBASE_ENV_ID = import.meta.env.VITE_CLOUDBASE_ENV_ID || "";
const CLOUDBASE_REGION = import.meta.env.VITE_CLOUDBASE_REGION || "ap-shanghai";
const TOKEN_KEY = "storyPlanetAdminToken";
let cloudbaseApp = null;

function phoneE164(value) {
  const digits = String(value || "").replace(/[^\d]/g, "");
  if (!digits) return "";
  if (digits.startsWith("86") && digits.length === 13) return `+${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+86${digits}`;
  return `+${digits}`;
}

function phoneForCloudBaseAuth(value) {
  const phone = phoneE164(value);
  if (phone.startsWith("+86")) return `+86 ${phone.slice(3)}`;
  return phone;
}

function cloudbaseClient() {
  if (!CLOUDBASE_ENV_ID) throw new Error("请先配置 VITE_CLOUDBASE_ENV_ID。");
  if (!cloudbaseApp) {
    cloudbaseApp = cloudbase.init({
      env: CLOUDBASE_ENV_ID,
      region: CLOUDBASE_REGION
    });
  }
  return cloudbaseApp;
}

function formatDate(value) {
  const timestamp = Number(value || 0);
  if (!timestamp) return "-";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(timestamp));
}

function formatBytes(value) {
  const size = Number(value || 0);
  if (!size) return "-";
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

function formatPercent(value) {
  return `${Math.round(Number(value || 0) * 100)}%`;
}

function cleanAssetName(value, fallback = "video") {
  return String(value || fallback)
    .replace(/^\/+/, "")
    .replace(/\.\./g, "")
    .replace(/[^a-zA-Z0-9._/-]/g, "-")
    .slice(0, 120) || fallback;
}

function randomId() {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID().slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10);
}

function videoCloudPath(file) {
  const filename = cleanAssetName(file.name || "video.mp4");
  const ext = filename.includes(".") ? filename.split(".").pop() : "mp4";
  return `learning-assets/admin/${Date.now()}-${randomId()}.${cleanAssetName(ext, "mp4")}`;
}

function readMp4Box(view, offset) {
  if (offset + 8 > view.byteLength) return null;
  let size = view.getUint32(offset);
  const type = String.fromCharCode(
    view.getUint8(offset + 4),
    view.getUint8(offset + 5),
    view.getUint8(offset + 6),
    view.getUint8(offset + 7)
  );
  let headerSize = 8;
  if (size === 1) {
    if (offset + 16 > view.byteLength) return null;
    const high = view.getUint32(offset + 8);
    const low = view.getUint32(offset + 12);
    size = high * 2 ** 32 + low;
    headerSize = 16;
  } else if (size === 0) {
    size = view.byteLength - offset;
  }
  if (!Number.isFinite(size) || size < headerSize) return null;
  return { type, size };
}

async function assertLearningVideoFastStart(file) {
  const filename = String(file && file.name || "");
  const mimeType = String(file && file.type || "");
  if (!/\.mp4$/i.test(filename) && mimeType !== "video/mp4") {
    throw new Error("学习视频请上传 MP4 文件。");
  }
  const head = await file.slice(0, Math.min(file.size, 8 * 1024 * 1024)).arrayBuffer();
  const view = new DataView(head);
  let offset = 0;
  while (offset + 8 <= view.byteLength) {
    const box = readMp4Box(view, offset);
    if (!box) break;
    if (box.type === "moov") return;
    if (box.type === "mdat") {
      throw new Error("这个 MP4 的 moov 元数据在视频数据后面，小程序会长时间缓冲。请先执行：ffmpeg -i input.mp4 -c copy -movflags +faststart output.mp4");
    }
    offset += box.size;
  }
  throw new Error("没有在视频开头找到 moov 元数据。请先转成 faststart MP4 后再上传。");
}

async function request(path, options = {}) {
  if (!API_BASE_URL) throw new Error("请先配置 VITE_ADMIN_API_BASE_URL。");
  const token = window.localStorage.getItem(TOKEN_KEY) || "";
  const headers = new Headers(options.headers || {});
  if (!headers.has("Content-Type") && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers
  });
  const text = await response.text();
  const data = text ? JSON.parse(text) : {};
  if (!response.ok || data.ok === false) {
    throw new Error(data.error || `请求失败：${response.status}`);
  }
  return data.data === undefined ? data : data.data;
}

function StatusPill({ value }) {
  const label = value === "published" ? "已发布" : value === "deleted" ? "已删除" : "草稿";
  return (
    <span className="inline-flex h-6 items-center rounded-full border border-zinc-200 bg-white px-2 text-xs text-zinc-700">
      {label}
    </span>
  );
}

function categoryId(category) {
  return category?.id || category?._id || "";
}

function categoryParents(categories) {
  return (categories || []).filter((item) => Number(item.level || 0) === 1);
}

function categoryChildren(categories, parentId) {
  return (categories || []).filter((item) => Number(item.level || 0) === 2 && item.parent_id === parentId);
}

function LoginScreen() {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const otpFlowRef = useRef(null);

  async function sendCode(event) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const auth = cloudbaseClient().auth();
      const { data, error: otpError } = await auth.signInWithOtp({
        phone: phoneForCloudBaseAuth(phone),
        options: {
          shouldCreateUser: false
        }
      });
      if (otpError) throw new Error(otpError.message || "验证码发送失败。");
      if (!data || typeof data.verifyOtp !== "function") throw new Error("CloudBase 没有返回验证码校验流程。");
      otpFlowRef.current = data;
      setCodeSent(true);
    } catch (err) {
      setError(err.message || "验证码发送失败。");
    } finally {
      setLoading(false);
    }
  }

  async function submitLogin(event) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (!otpFlowRef.current) throw new Error("请先发送短信验证码。");
      const app = cloudbaseClient();
      const { error: loginError } = await otpFlowRef.current.verifyOtp({ token: code });
      if (loginError) throw new Error(loginError.message || "验证码不正确。");
      await app.auth().getSession();
      const sessionResult = await app.callFunction({
        name: "admin",
        data: {
          action: "createAdminSessionFromCloudBaseAuth",
          phone: phoneE164(phone)
        }
      });
      const result = sessionResult.result || {};
      if (!result.ok) throw new Error(result.error || "管理员登录失败。");
      if (!result.data || !result.data.token) throw new Error("管理员登录没有返回 token。");
      window.localStorage.setItem(TOKEN_KEY, result.data.token);
      window.location.reload();
    } catch (err) {
      setError(err.message || "验证码不正确。");
      setLoading(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#fafafa] px-8">
      <form onSubmit={submitLogin} className="w-full max-w-[420px] rounded-lg border border-zinc-200 bg-white p-8 shadow-surface">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md border border-zinc-200 bg-zinc-950 text-white">
            <Shield size={18} />
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-normal text-zinc-950">Story Planet Admin</h1>
            <p className="mt-1 text-sm text-zinc-500">仅管理员可访问</p>
          </div>
        </div>

        <div className="mb-4 space-y-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-zinc-800">管理员手机号</span>
            <input
              type="tel"
              required
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              className="h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-950 placeholder:text-zinc-400"
              placeholder="13800138000"
            />
          </label>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-zinc-800">短信验证码</span>
              <input
                type="text"
                inputMode="numeric"
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
                className="h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-950 placeholder:text-zinc-400"
                placeholder="6 位验证码"
              />
            </label>
            <button
              type="button"
              onClick={sendCode}
              disabled={loading || !phone}
              className="mt-6 h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            >
              {codeSent ? "重新发送" : "发送验证码"}
            </button>
          </div>
          <p className="text-xs text-zinc-500">请先在 CloudBase 开启短信验证码登录，并在小程序账号页绑定手机号。后台权限由用户记录的 admin 标记控制。</p>
        </div>

        <button
          type="submit"
          disabled={loading || !codeSent}
          className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-zinc-950 px-4 text-sm font-medium text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? <Loader2 className="animate-spin" size={16} /> : <Shield size={16} />}
          登录后台
        </button>

        {error ? (
          <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        ) : null}

        {!API_BASE_URL ? (
          <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            需要在 Cloudflare Web 项目中配置 VITE_ADMIN_API_BASE_URL。
          </p>
        ) : null}
        {!CLOUDBASE_ENV_ID ? (
          <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            需要在 Cloudflare Web 项目中配置 VITE_CLOUDBASE_ENV_ID。
          </p>
        ) : null}
      </form>
    </main>
  );
}

function Header({ admin, onRefresh, onLogout, loading }) {
  return (
    <header className="sticky top-0 z-20 border-b border-zinc-200 bg-white/90 backdrop-blur">
      <div className="flex h-14 items-center justify-between px-6">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-md border border-zinc-200 bg-zinc-950 text-white">
            <Shield size={16} />
          </div>
          <div>
            <p className="text-sm font-medium text-zinc-950">Story Planet Admin</p>
            <p className="text-xs text-zinc-500">{admin?.phone || admin?.email || admin?.openid || "管理员"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex h-8 items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-700 hover:bg-zinc-50"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            刷新
          </button>
          <button
            type="button"
            onClick={onLogout}
            className="inline-flex h-8 items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-700 hover:bg-zinc-50"
          >
            <LogOut size={14} />
            退出
          </button>
        </div>
      </div>
    </header>
  );
}

function Sidebar({ active, setActive, counts }) {
  const items = [
    { id: "users", label: "用户管理", icon: Users, count: counts.users },
    { id: "videos", label: "视频管理", icon: Film, count: counts.videos },
    { id: "categories", label: "分类管理", icon: FolderTree, count: counts.categories }
  ];
  return (
    <aside className="w-60 border-r border-zinc-200 bg-white px-3 py-4">
      <nav className="space-y-1">
        {items.map((item) => {
          const Icon = item.icon;
          const selected = active === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setActive(item.id)}
              className={`flex h-9 w-full items-center justify-between rounded-md px-3 text-sm transition ${
                selected ? "bg-zinc-950 text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950"
              }`}
            >
              <span className="flex items-center gap-2">
                <Icon size={15} />
                {item.label}
              </span>
              <span className={selected ? "text-zinc-300" : "text-zinc-400"}>{item.count}</span>
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

function UsersView({ users, query, setQuery, onRoleChange, roleSavingId }) {
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((user) => {
      return [user._id, user.openid, user.unionid, user.referral_code, user.nickname, user.phone_e164, user.phone_number, user.role]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [query, users]);

  return (
    <section className="flex min-w-0 flex-1 flex-col">
      <div className="border-b border-zinc-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-zinc-950">用户管理</h2>
            <p className="mt-1 text-sm text-zinc-500">查看平台所有 CloudBase 用户和管理员状态。</p>
          </div>
          <div className="relative w-72">
            <Search className="pointer-events-none absolute left-3 top-2.5 text-zinc-400" size={15} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="h-9 w-full rounded-md border border-zinc-200 bg-white pl-9 pr-3 text-sm text-zinc-950 placeholder:text-zinc-400"
              placeholder="搜索 openid / 手机号 / referral"
            />
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-surface">
          <div className="table-grid border-b border-zinc-200 bg-zinc-50 px-4 py-2 text-xs font-medium text-zinc-500">
            <span>用户</span>
            <span>角色</span>
            <span>邀请码</span>
            <span>最近登录</span>
            <span>创建时间</span>
          </div>
          {filtered.map((user) => (
            <div key={user._id} className="table-grid items-center border-b border-zinc-100 px-4 py-3 text-sm last:border-b-0">
              <div className="min-w-0">
                <p className="truncate font-medium text-zinc-950">{user.nickname || user._id}</p>
                <p className="truncate font-mono text-xs text-zinc-500">{user.openid || "-"}</p>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <select
                    value={user.role || "user"}
                    disabled={roleSavingId === user._id}
                    onChange={(event) => onRoleChange(user._id, event.target.value)}
                    className="h-8 rounded-md border border-zinc-200 bg-white px-2 text-sm text-zinc-800 disabled:opacity-60"
                  >
                    <option value="user">普通用户</option>
                    <option value="member">会员</option>
                    <option value="admin">管理员</option>
                  </select>
                  {user.role === "admin" ? (
                    <span className="inline-flex h-6 items-center gap-1 rounded-full border border-zinc-900 bg-zinc-950 px-2 text-xs text-white">
                      <CheckCircle2 size={12} />
                      admin
                    </span>
                  ) : null}
                </div>
              </div>
              <span className="font-mono text-xs text-zinc-500">{user.phone_e164 || user.phone_number || user.referral_code || "-"}</span>
              <span className="text-zinc-600">{formatDate(user.last_login_at)}</span>
              <span className="text-zinc-600">{formatDate(user.created_at)}</span>
            </div>
          ))}
          {!filtered.length ? <div className="px-4 py-10 text-center text-sm text-zinc-500">没有匹配用户</div> : null}
        </div>
      </div>
    </section>
  );
}

function LearningVideoModal({ open, mode, video, categories, onClose, onSaved }) {
  const parents = categoryParents(categories);
  const initialParentId = video?.category_parent_id || categoryId(parents[0]);
  const [form, setForm] = useState({
    title: video?.title || "",
    subtitle: video?.subtitle || "",
    description: video?.description || "",
    level: video?.level || "",
    visibility: video?.visibility || "public",
    status: video?.status || "published",
    category_parent_id: initialParentId,
    category_id: video?.category_id || ""
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const nextParentId = video?.category_parent_id || categoryId(parents[0]);
    const children = categoryChildren(categories, nextParentId);
    setForm({
      title: video?.title || "",
      subtitle: video?.subtitle || "",
      description: video?.description || "",
      level: video?.level || "",
      visibility: video?.visibility || "public",
      status: video?.status || "published",
      category_parent_id: nextParentId,
      category_id: video?.category_id || categoryId(children[0])
    });
    setFile(null);
    setProgress(0);
    setError("");
  }, [open, video, categories]);

  if (!open) return null;

  const children = categoryChildren(categories, form.category_parent_id);

  function updateParent(parentId) {
    const nextChildren = categoryChildren(categories, parentId);
    setForm({ ...form, category_parent_id: parentId, category_id: categoryId(nextChildren[0]) });
  }

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setProgress(0);
    setError("");
    try {
      if (!form.category_id) throw new Error("请选择二级分类。");
      if (mode === "edit") {
        const saved = await request(`/videos/${encodeURIComponent(video.id || video._id)}`, {
          method: "PATCH",
          body: JSON.stringify(form)
        });
        onSaved(saved);
        onClose();
        return;
      }

      if (!file) throw new Error("请选择视频文件。");
      if (!String(file.type || "").startsWith("video/")) throw new Error("仅支持上传视频文件。");
      await assertLearningVideoFastStart(file);
      const cloudPath = videoCloudPath(file);
      const uploaded = await cloudbaseClient().uploadFile({
        cloudPath,
        filePath: file,
        method: "put",
        onUploadProgress(event) {
          if (event.total) setProgress(Math.round((event.loaded / event.total) * 100));
        }
      });
      if (!uploaded || !uploaded.fileID) throw new Error("CloudBase Storage 没有返回文件 ID。");
      const saved = await request("/videos", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          video_file_id: uploaded.fileID,
          cloud_path: cloudPath,
          file_name: file.name,
          file_size: file.size,
          mime_type: file.type || "video/mp4"
        })
      });
      onSaved(saved);
      onClose();
    } catch (err) {
      setError(err.message || "保存失败。");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 px-6">
      <form onSubmit={submit} className="w-full max-w-2xl rounded-lg border border-zinc-200 bg-white p-5 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-zinc-950">{mode === "edit" ? "修改视频" : "上传视频"}</h3>
            <p className="mt-1 text-sm text-zinc-500">发布视频必须选择二级分类，前端会按分类展示。</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md px-2 py-1 text-sm text-zinc-500 hover:bg-zinc-100">
            关闭
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-zinc-800">标题</span>
            <input required value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm" />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-zinc-800">副标题</span>
            <input value={form.subtitle} onChange={(event) => setForm({ ...form, subtitle: event.target.value })} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm" />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-zinc-800">一级分类</span>
            <select required value={form.category_parent_id} onChange={(event) => updateParent(event.target.value)} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm">
              <option value="">请选择一级分类</option>
              {parents.map((parent) => <option key={categoryId(parent)} value={categoryId(parent)}>{parent.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-zinc-800">二级分类</span>
            <select required value={form.category_id} onChange={(event) => setForm({ ...form, category_id: event.target.value })} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm">
              <option value="">请选择二级分类</option>
              {children.map((child) => <option key={categoryId(child)} value={categoryId(child)}>{child.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-zinc-800">级别</span>
            <input value={form.level} onChange={(event) => setForm({ ...form, level: event.target.value })} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm" />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-zinc-800">可见性</span>
            <select value={form.visibility} onChange={(event) => setForm({ ...form, visibility: event.target.value })} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm">
              <option value="public">公开</option>
              <option value="private">隐藏</option>
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-zinc-800">状态</span>
            <select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm">
              <option value="published">已发布</option>
              <option value="hidden">已下架</option>
              <option value="draft">草稿</option>
            </select>
          </label>
        </div>

        <label className="mt-4 block">
          <span className="mb-1 block text-sm font-medium text-zinc-800">描述</span>
          <textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} className="min-h-20 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm" />
        </label>

        {mode !== "edit" ? (
          <label className="mt-4 block rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-4">
            <span className="mb-2 flex items-center gap-2 text-sm font-medium text-zinc-800">
              <Upload size={15} />
              视频文件
            </span>
            <input type="file" accept="video/*" onChange={(event) => setFile(event.target.files?.[0] || null)} className="text-sm text-zinc-600" />
            {file ? <p className="mt-2 text-xs text-zinc-500">{file.name} · {formatBytes(file.size)}</p> : null}
            {saving && progress > 0 ? (
              <div className="mt-3">
                <div className="h-1.5 overflow-hidden rounded-full bg-zinc-200">
                  <div className="h-full rounded-full bg-zinc-950 transition-all" style={{ width: `${progress}%` }} />
                </div>
                <p className="mt-1 text-xs text-zinc-500">上传中 {progress}%</p>
              </div>
            ) : null}
          </label>
        ) : null}

        {error ? <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 rounded-md border border-zinc-200 px-3 text-sm text-zinc-700 hover:bg-zinc-50">取消</button>
          <button type="submit" disabled={saving} className="inline-flex h-9 items-center gap-2 rounded-md bg-zinc-950 px-3 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={15} /> : mode === "edit" ? <Pencil size={15} /> : <Upload size={15} />}
            {mode === "edit" ? "保存修改" : "发布"}
          </button>
        </div>
      </form>
    </div>
  );
}

function VideoDetailsModal({ video, onClose }) {
  const [details, setDetails] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!video) return;
    setDetails(null);
    setError("");
    request(`/videos/${encodeURIComponent(video.id || video._id)}/progress`)
      .then(setDetails)
      .catch((err) => setError(err.message || "加载详情失败。"));
  }, [video]);

  if (!video) return null;

  const stats = details?.stats || {};
  const rows = details?.rows || [];

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 px-6">
      <div className="w-full max-w-4xl rounded-lg border border-zinc-200 bg-white p-5 shadow-xl">
        <div className="mb-5 flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-zinc-950">{video.title}</h3>
            <p className="mt-1 text-sm text-zinc-500">学习详情和播放进度</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md px-2 py-1 text-sm text-zinc-500 hover:bg-zinc-100">关闭</button>
        </div>

        {error ? <p className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}
        {!details && !error ? <div className="py-10 text-center text-sm text-zinc-500">加载中...</div> : null}
        {details ? (
          <>
            <div className="grid grid-cols-4 gap-3">
              <div className="rounded-lg border border-zinc-200 p-3"><p className="text-xs text-zinc-500">学习人数</p><p className="mt-1 text-xl font-semibold">{stats.learner_count || 0}</p></div>
              <div className="rounded-lg border border-zinc-200 p-3"><p className="text-xs text-zinc-500">播放次数</p><p className="mt-1 text-xl font-semibold">{stats.play_count || 0}</p></div>
              <div className="rounded-lg border border-zinc-200 p-3"><p className="text-xs text-zinc-500">完成次数</p><p className="mt-1 text-xl font-semibold">{stats.completed_count || 0}</p></div>
              <div className="rounded-lg border border-zinc-200 p-3"><p className="text-xs text-zinc-500">平均进度</p><p className="mt-1 text-xl font-semibold">{formatPercent(stats.avg_progress_ratio)}</p></div>
            </div>
            <div className="mt-5 max-h-[420px] overflow-auto rounded-lg border border-zinc-200">
              <div className="grid grid-cols-6 gap-3 border-b border-zinc-200 bg-zinc-50 px-4 py-2 text-xs font-medium text-zinc-500">
                <span>用户</span><span>孩子</span><span>进度</span><span>播放</span><span>完成</span><span>最近学习</span>
              </div>
              {rows.map((row) => (
                <div key={row.id} className="grid grid-cols-6 gap-3 border-b border-zinc-100 px-4 py-3 text-sm last:border-b-0">
                  <span className="truncate text-zinc-950">{row.nickname}</span>
                  <span className="truncate text-zinc-600">{row.child_nickname || "-"}</span>
                  <span>{formatPercent(row.progress_ratio)}</span>
                  <span>{row.play_count}</span>
                  <span>{row.completed_count}</span>
                  <span className="text-zinc-500">{formatDate(row.last_played_at)}</span>
                </div>
              ))}
              {!rows.length ? <div className="px-4 py-10 text-center text-sm text-zinc-500">还没有学习记录</div> : null}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function CategoryModal({ open, level, parent, parents, saving, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setName("");
    setParentId(categoryId(parent));
    setError("");
  }, [open, level, parent]);

  if (!open) return null;

  const isChild = level === 2;
  const selectedParent = (parents || []).find((item) => categoryId(item) === parentId);

  function submit(event) {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("请输入分类名称。");
      return;
    }
    if (isChild && !parentId) {
      setError("请先选择一级分类。");
      return;
    }
    onSubmit({ name: trimmed, level, parent_id: isChild ? parentId : "" });
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 px-6">
      <form onSubmit={submit} className="w-full max-w-md rounded-lg border border-zinc-200 bg-white p-5 shadow-xl">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-zinc-950">{isChild ? "新增二级分类" : "新增一级分类"}</h3>
            <p className="mt-1 text-sm text-zinc-500">
              {isChild ? "二级分类必须归属于一个一级分类。" : "一级分类用于组织一组二级学习主题。"}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md px-2 py-1 text-sm text-zinc-500 hover:bg-zinc-100">
            关闭
          </button>
        </div>

        {isChild ? (
          <label className="mb-4 block">
            <span className="mb-1 block text-sm font-medium text-zinc-800">归属一级分类</span>
            <select
              required
              value={parentId}
              onChange={(event) => {
                setParentId(event.target.value);
                setError("");
              }}
              className="h-10 w-full rounded-md border border-zinc-200 px-3 text-sm outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10"
            >
              <option value="">请选择一级分类</option>
              {(parents || []).map((item) => (
                <option key={categoryId(item)} value={categoryId(item)}>
                  {item.name}
                </option>
              ))}
            </select>
            {selectedParent ? <p className="mt-1 text-xs text-zinc-500">当前归属于：{selectedParent.name}</p> : null}
          </label>
        ) : null}

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-zinc-800">分类名称</span>
          <input
            autoFocus
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={isChild ? "例如 入门发音" : "例如 自然拼读"}
            className="h-10 w-full rounded-md border border-zinc-200 px-3 text-sm outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10"
          />
        </label>

        {error ? <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 rounded-md border border-zinc-200 px-3 text-sm text-zinc-700 hover:bg-zinc-50">
            取消
          </button>
          <button type="submit" disabled={saving} className="inline-flex h-9 items-center gap-2 rounded-md bg-zinc-950 px-3 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />}
            创建
          </button>
        </div>
      </form>
    </div>
  );
}

function CategoryManager({ categories, setCategories, reload }) {
  const parents = categoryParents(categories);
  const [activeParentId, setActiveParentId] = useState(categoryId(parents[0]));
  const [modal, setModal] = useState({ open: false, level: 1 });
  const [saving, setSaving] = useState(false);
  const activeParent = parents.find((parent) => categoryId(parent) === activeParentId) || parents[0];
  const activeId = categoryId(activeParent);
  const children = categoryChildren(categories, activeId);

  useEffect(() => {
    if (!activeParentId && parents[0]) setActiveParentId(categoryId(parents[0]));
    if (activeParentId && !parents.some((parent) => categoryId(parent) === activeParentId)) {
      setActiveParentId(categoryId(parents[0]));
    }
  }, [categories]);

  async function createCategory(data) {
    setSaving(true);
    try {
      const created = await request("/categories", {
        method: "POST",
        body: JSON.stringify(data)
      });
      setModal({ open: false, level: 1 });
      await reload();
      if (Number(data.level || 0) === 1) setActiveParentId(categoryId(created));
      if (Number(data.level || 0) === 2) setActiveParentId(data.parent_id || "");
    } finally {
      setSaving(false);
    }
  }

  async function patchCategory(category, patch) {
    setSaving(true);
    try {
      const updated = await request(`/categories/${encodeURIComponent(categoryId(category))}`, {
        method: "PATCH",
        body: JSON.stringify(patch)
      });
      setCategories((items) => items.map((item) => (categoryId(item) === categoryId(updated) ? updated : item)));
    } finally {
      setSaving(false);
    }
  }

  async function deleteCategory(category) {
    if (!window.confirm("确定删除这个分类？分类下有视频时会被后台阻止。")) return;
    setSaving(true);
    try {
      await request(`/categories/${encodeURIComponent(categoryId(category))}`, { method: "DELETE" });
      reload();
    } finally {
      setSaving(false);
    }
  }

  function categoryRow(category) {
    const isParent = Number(category.level || 0) === 1;
    const isActive = isParent && activeId === categoryId(category);
    return (
      <div key={categoryId(category)} className={`flex items-center justify-between gap-3 border-b border-zinc-100 px-4 py-3 last:border-b-0 ${isActive ? "bg-zinc-50" : ""}`}>
        <button type="button" onClick={() => isParent && setActiveParentId(categoryId(category))} className={`min-w-0 flex-1 truncate text-left text-sm font-medium ${isActive ? "text-zinc-950" : "text-zinc-600"}`}>
          {category.name}
        </button>
        <div className="flex items-center gap-2">
          <button disabled={saving} onClick={() => patchCategory(category, { status: category.status === "hidden" ? "active" : "hidden" })} className="h-8 rounded-md border border-zinc-200 px-2 text-xs text-zinc-700 hover:bg-zinc-50">
            {category.status === "hidden" ? "显示" : "隐藏"}
          </button>
          <button disabled={saving} onClick={() => {
            const name = window.prompt("分类名称", category.name);
            if (name) patchCategory(category, { name });
          }} className="rounded-md border border-zinc-200 p-1.5 text-zinc-600 hover:bg-zinc-50" aria-label="重命名">
            <Pencil size={14} />
          </button>
          <button disabled={saving} onClick={() => deleteCategory(category)} className="rounded-md border border-zinc-200 p-1.5 text-zinc-600 hover:bg-zinc-50" aria-label="删除">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    );
  }

  function openCreateModal(level) {
    if (level === 2 && !activeId) return;
    setModal({ open: true, level });
  }

  return (
    <section className="flex min-w-0 flex-1 flex-col">
      <div className="border-b border-zinc-200 bg-white px-6 py-4">
        <h2 className="text-lg font-semibold text-zinc-950">分类管理</h2>
        <p className="mt-1 text-sm text-zinc-500">英语学习视频使用两级分类；上传视频时必须选择二级分类。</p>
      </div>
      <div className="grid flex-1 grid-cols-2 gap-5 p-6">
        <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-surface">
          <div className="flex items-center justify-between border-b border-zinc-200 bg-zinc-50 px-4 py-3">
            <span className="text-sm font-semibold">一级分类</span>
            <button type="button" disabled={saving} onClick={() => openCreateModal(1)} className="inline-flex h-8 items-center gap-1 rounded-md bg-zinc-950 px-2.5 text-xs font-medium text-white hover:bg-zinc-800 disabled:opacity-60">
              <Plus size={14} />
              新增
            </button>
          </div>
          {parents.map(categoryRow)}
          {!parents.length ? <div className="px-4 py-10 text-center text-sm text-zinc-500">还没有一级分类</div> : null}
        </div>
        <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-surface">
          <div className="flex items-center justify-between border-b border-zinc-200 bg-zinc-50 px-4 py-3">
            <span className="min-w-0 truncate text-sm font-semibold">{activeParent?.name || "选择一级分类"} / 二级分类</span>
            <button type="button" disabled={saving || !activeId} onClick={() => openCreateModal(2)} className="inline-flex h-8 items-center gap-1 rounded-md bg-zinc-950 px-2.5 text-xs font-medium text-white hover:bg-zinc-800 disabled:opacity-50">
              <Plus size={14} />
              新增
            </button>
          </div>
          {children.map(categoryRow)}
          {!activeId ? <div className="px-4 py-10 text-center text-sm text-zinc-500">请先选择一级分类</div> : null}
          {activeId && !children.length ? <div className="px-4 py-10 text-center text-sm text-zinc-500">这个一级分类下还没有二级分类</div> : null}
        </div>
      </div>
      <CategoryModal
        open={modal.open}
        level={modal.level}
        parent={activeParent}
        parents={parents}
        saving={saving}
        onClose={() => setModal({ open: false, level: 1 })}
        onSubmit={createCategory}
      />
    </section>
  );
}

function LearningVideosView({ videos, setVideos, categories, reload }) {
  const [modal, setModal] = useState({ open: false, mode: "create", video: null });
  const [detailsVideo, setDetailsVideo] = useState(null);
  const [savingId, setSavingId] = useState("");

  const groupedVideos = useMemo(() => {
    const groups = {};
    videos.forEach((video) => {
      const parent = video.category_parent_name || "未分类";
      const child = video.category_name || "默认";
      const key = `${parent}/${child}`;
      if (!groups[key]) groups[key] = { parent, child, videos: [] };
      groups[key].videos.push(video);
    });
    return Object.values(groups);
  }, [videos]);

  async function patchVideo(id, patch) {
    setSavingId(id);
    try {
      const updated = await request(`/videos/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(patch)
      });
      setVideos((items) => items.map((item) => (item.id === id || item._id === id ? updated : item)));
    } finally {
      setSavingId("");
    }
  }

  async function deleteVideo(id) {
    if (!window.confirm("确定删除这个视频？")) return;
    setSavingId(id);
    try {
      await request(`/videos/${encodeURIComponent(id)}`, { method: "DELETE" });
      setVideos((items) => items.filter((item) => item.id !== id && item._id !== id));
    } finally {
      setSavingId("");
    }
  }

  function saveVideo(video) {
    setVideos((items) => {
      const id = video.id || video._id;
      const exists = items.some((item) => item.id === id || item._id === id);
      return exists ? items.map((item) => (item.id === id || item._id === id ? video : item)) : [video, ...items];
    });
  }

  return (
    <section className="flex min-w-0 flex-1 flex-col">
      <div className="border-b border-zinc-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-zinc-950">视频管理</h2>
            <p className="mt-1 text-sm text-zinc-500">按分类管理、查看学习数据、修改视频信息。</p>
          </div>
          <button type="button" onClick={() => setModal({ open: true, mode: "create", video: null })} className="inline-flex h-9 items-center gap-2 rounded-md bg-zinc-950 px-3 text-sm font-medium text-white hover:bg-zinc-800">
            <Plus size={15} />
            上传视频
          </button>
        </div>
      </div>

      <div className="space-y-5 p-6">
        {groupedVideos.map((group) => (
          <div key={`${group.parent}/${group.child}`} className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-surface">
            <div className="border-b border-zinc-200 bg-zinc-50 px-4 py-3">
              <p className="text-sm font-semibold text-zinc-950">{group.parent} / {group.child}</p>
              <p className="text-xs text-zinc-500">{group.videos.length} 个视频</p>
            </div>
            <div className="video-grid border-b border-zinc-200 bg-zinc-50 px-4 py-2 text-xs font-medium text-zinc-500">
              <span>预览</span><span>视频</span><span>状态</span><span>学习数据</span><span>操作</span>
            </div>
            {group.videos.map((video) => {
              const id = video.id || video._id;
              const disabled = savingId === id;
              return (
                <div key={id} className="video-grid items-center border-b border-zinc-100 px-4 py-3 text-sm last:border-b-0">
                  <div className="h-11 w-16 overflow-hidden rounded-md border border-zinc-200 bg-zinc-100">
                    {video.cover_url ? <img src={video.cover_url} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-zinc-400"><Film size={18} /></div>}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-medium text-zinc-950">{video.title}</p>
                    <p className="truncate text-xs text-zinc-500">{video.description || video.subtitle || video.video_file_id}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusPill value={video.status} />
                    <span className="text-xs text-zinc-400">{video.visibility || "public"}</span>
                  </div>
                  <div className="text-xs text-zinc-600">
                    <p>{video.learner_count || 0} 人 · 播放 {video.play_count || 0}</p>
                    <p>完成 {video.completed_count || 0} · 平均 {formatPercent(video.avg_progress_ratio)}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button disabled={disabled} onClick={() => setDetailsVideo(video)} className="inline-flex h-8 items-center gap-1 rounded-md border border-zinc-200 px-2 text-xs text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"><BarChart3 size={14} />详情</button>
                    <button disabled={disabled} onClick={() => setModal({ open: true, mode: "edit", video })} className="inline-flex h-8 items-center gap-1 rounded-md border border-zinc-200 px-2 text-xs text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"><Pencil size={14} />修改</button>
                    <button disabled={disabled} onClick={() => patchVideo(id, { status: video.status === "published" ? "hidden" : "published" })} className="h-8 rounded-md border border-zinc-200 px-2 text-xs text-zinc-700 hover:bg-zinc-50 disabled:opacity-40">{video.status === "published" ? "下架" : "上架"}</button>
                    <button disabled={disabled} onClick={() => deleteVideo(id)} className="rounded-md border border-zinc-200 p-1.5 text-zinc-600 hover:bg-zinc-50 disabled:opacity-40" aria-label="删除"><Trash2 size={14} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        {!videos.length ? <div className="rounded-lg border border-zinc-200 bg-white px-4 py-10 text-center text-sm text-zinc-500">还没有视频</div> : null}
      </div>

      <LearningVideoModal open={modal.open} mode={modal.mode} video={modal.video} categories={categories} onClose={() => setModal({ open: false, mode: "create", video: null })} onSaved={saveVideo} />
      <VideoDetailsModal video={detailsVideo} onClose={() => setDetailsVideo(null)} />
    </section>
  );
}

export default function App() {
  const [token, setToken] = useState(() => window.localStorage.getItem(TOKEN_KEY) || "");
  const [active, setActive] = useState("users");
  const [admin, setAdmin] = useState(null);
  const [users, setUsers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [roleSavingId, setRoleSavingId] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const callbackToken = params.get("token");
    const callbackError = params.get("error");
    if (callbackToken) {
      window.localStorage.setItem(TOKEN_KEY, callbackToken);
      setToken(callbackToken);
      window.history.replaceState({}, "", "/");
    } else if (callbackError) {
      setError(decodeURIComponent(callbackError));
      window.history.replaceState({}, "", "/");
    }
  }, []);

  async function loadAll() {
    if (!window.localStorage.getItem(TOKEN_KEY)) return;
    setLoading(true);
    setError("");
    try {
      const [me, userRows, videoRows, categoryRows] = await Promise.all([
        request("/me"),
        request("/users"),
        request("/videos"),
        request("/categories")
      ]);
      setAdmin(me);
      setUsers(Array.isArray(userRows) ? userRows : []);
      setVideos(Array.isArray(videoRows) ? videoRows : []);
      setCategories(Array.isArray(categoryRows) ? categoryRows : []);
    } catch (err) {
      setError(err.message || "加载失败。");
      if (/登录|token|权限|Unauthorized/i.test(err.message || "")) {
        window.localStorage.removeItem(TOKEN_KEY);
        setToken("");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, [token]);

  function logout() {
    window.localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAdmin(null);
    setCategories([]);
  }

  async function updateUserRole(userId, role) {
    if (!userId || !role) return;
    setRoleSavingId(userId);
    setError("");
    try {
      const updated = await request(`/users/${encodeURIComponent(userId)}/role`, {
        method: "PATCH",
        body: JSON.stringify({ role })
      });
      setUsers((current) => current.map((user) => user._id === userId ? { ...user, ...updated } : user));
    } catch (err) {
      setError(err.message || "保存用户角色失败。");
    } finally {
      setRoleSavingId("");
    }
  }

  if (!token) return <LoginScreen />;

  return (
    <div className="min-h-screen bg-[#fafafa] text-zinc-950">
      <Header admin={admin} onRefresh={loadAll} onLogout={logout} loading={loading} />
      {error ? (
        <div className="border-b border-red-200 bg-red-50 px-6 py-2 text-sm text-red-700">{error}</div>
      ) : null}
      <div className="flex min-h-[calc(100vh-56px)]">
        <Sidebar active={active} setActive={setActive} counts={{ users: users.length, videos: videos.length, categories: categories.length }} />
        {active === "users" ? (
          <UsersView users={users} query={query} setQuery={setQuery} onRoleChange={updateUserRole} roleSavingId={roleSavingId} />
        ) : active === "categories" ? (
          <CategoryManager categories={categories} setCategories={setCategories} reload={loadAll} />
        ) : (
          <LearningVideosView videos={videos} setVideos={setVideos} categories={categories} reload={loadAll} />
        )}
      </div>
    </div>
  );
}
